import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import { Users, Target, Heart, Award } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function About() {
  return (
    <div className="bg-brand-black min-h-screen">
      <SEO
        title="About Delta BJJ: Our Story & Philosophy | Delta BJJ"
        description="Delta BJJ is a Culver City hub for authentic Brazilian Jiu-Jitsu. Meet our Jiu-Jitsu instructors, learn our philosophy, and the best reasons to join the team!"
        url="https://www.deltabjj.com/about-delta-bjj-culver-city/"
      />

      {/* Hero Header */}
      <section className="relative pt-48 pb-20 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <FadeInWhenVisible>
            <span className="text-brand-silver font-display text-xs tracking-[0.4em] uppercase mb-6 block font-bold">Our Story</span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-9xl font-display font-bold text-white tracking-tighter leading-tight mb-8 break-words px-4">
              OUR ROOTS, OUR MATS: <br />
              <span className="blue-gradient-text">THE STORY OF DELTA BJJ</span>
            </h1>
            <p className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto font-light leading-relaxed">
              Welcome to Delta BJJ. Whether you are stepping onto the mats for the first time or you are a seasoned competitor, you aren't just joining a gym, you're joining a lineage and a community.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* The Delta BJJ Foundation */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-brand-black mb-6 tracking-tighter">
              The Delta BJJ <span className="text-gray-300">Foundation</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-4 max-w-4xl">
              Delta BJJ was built on the belief that Jiu-Jitsu is for everyone. Founded by Gerson Sanginitto, our academy is the result of decades of high-level experience and a passion for teaching. Gerson's vision was to create a space where world-class technique meets a supportive, ego-free environment.
            </p>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-4xl">
              We don't just teach "moves"; we teach the fundamental mechanics that allow a smaller, weaker person to successfully defend against a larger opponent.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-brand-black mb-6 tracking-tighter">
              Our Philosophy: <span className="text-gray-300">More Than Just Combat</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-4xl">
              At Delta BJJ, we view the mat as a microcosm of life. The discipline, resilience, and problem-solving skills you develop during a tough rolling session are the same tools that help you excel in your professional and personal life. Our philosophy is rooted in:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: Award,
                  title: "Technical Excellence",
                  description: "We prioritize Jiu-Jitsu, the leverage and pressure that make the art effective."
                },
                {
                  icon: Users,
                  title: "Mutual Respect",
                  description: "We leave our egos at the door. We are here to sharpen one another, not tear each other down."
                },
                {
                  icon: Target,
                  title: "Lifelong Learning",
                  description: "From white belt to black belt, we remain students of the art."
                }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <FadeInWhenVisible key={i} delay={i * 0.1}>
                    <div className="bg-white p-6 rounded-sm border border-gray-100 shadow-sm h-full hover:shadow-md transition-shadow">
                      <div className="w-14 h-14 bg-brand-blue rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-display font-bold text-brand-black mb-3 tracking-tighter">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {item.description}
                      </p>
                    </div>
                  </FadeInWhenVisible>
                );
              })}
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Our Affiliations */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-brand-black mb-6 tracking-tighter">
              Our Affiliations & <span className="text-gray-300">Global Network</span>
            </h2>
            <div className="max-w-4xl">
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-4">
                We are proud to be a cornerstone of the{' '}
                <a 
                  href="https://shop-r1ngbjj.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-brand-blue hover:underline font-semibold"
                >
                  R1ng Global
                </a>{' '}
                organization. Under Gerson Sanginitto's leadership, our affiliation with R1 ensures that our students are learning the most current and effective techniques being used in the BJJ world today.
              </p>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Being part of a larger network means our students have access to a massive community of practitioners and a standard of excellence that is recognized worldwide.
              </p>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Dedicated to the Community */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-brand-black text-white border-b border-white/10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-transparent to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <FadeInWhenVisible>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 bg-brand-blue rounded-lg flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-white mb-6 tracking-tighter">
                  Dedicated to the <span className="blue-gradient-text">Community</span>
                </h2>
                <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-4xl">
                  Delta BJJ isn't just a business; it's a local hub within the Culver City and Brazilian Jiu-Jitsu communities. We are deeply committed to our neighborhood, partnering with local organizations and providing a safe, constructive outlet for youth and adults alike. We believe that a strong academy builds a stronger community.
                </p>
              </div>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto text-center">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold text-brand-black mb-6 tracking-tighter">
              Meet the <span className="text-gray-300">Team</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Our coaching staff is comprised of dedicated practitioners who live and breathe the BJJ lifestyle.
            </p>
            <Link 
              to="/instructors" 
              className="btn-premium-blue inline-flex items-center gap-3"
            >
              Meet our Instructors
              <Users className="w-5 h-5" />
            </Link>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Join the Family CTA */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-brand-black text-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold mb-6 tracking-tighter">
              Join the <span className="blue-gradient-text">Family</span>
            </h2>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-2xl mx-auto">
              The hardest part of Jiu-Jitsu is walking through the front door. We'll take it from there.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link 
                to="/contact-bjj-culver-city" 
                className="btn-premium-outline"
              >
                Contact us today to schedule your first class
              </Link>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Footer Separator - Clear visual break */}
      <div className="h-1 bg-gradient-to-r from-transparent via-brand-blue/30 to-transparent"></div>
    </div>
  );
}
